import SeoHead from "@/components/SeoHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import LocalFaqSection from "./components/LocalFaqSection";

const localServices = [
  {
    icon: "ri-home-4-line",
    title: "Pose de carrelage à Sierre",
    desc: "Pose de carrelage à Sierre et dans les communes voisines. On maîtrise les grands formats et les finitions modernes, mais aussi les vieilles surfaces irrégulières.",
  },
  {
    icon: "ri-drop-line",
    title: "Rénovation salle de bain à Sierre",
    desc: "On refait des salles de bain à Sierre. Démolition, étanchéité, carrelage, installation sanitaire — on gère tout pour que ce soit carré.",
  },
  {
    icon: "ri-water-flash-line",
    title: "Douche italienne à Sierre",
    desc: "Douche italienne à Sierre. Receveur encastré, pente bien calculée, joints époxy solides. C'est beau quand c'est bien fait, et ça dure.",
  },
  {
    icon: "ri-layout-grid-line",
    title: "Faïence & mosaïque à Sierre",
    desc: "Faïence et mosaïque à Sierre. Des crédences de cuisine et des salles de bain avec du caractère. On travaille avec les matériaux qui correspondent à votre projet.",
  },
  {
    icon: "ri-sun-line",
    title: "Carrelage extérieur & terrasses à Sierre",
    desc: "Terrasses et allées à Sierre. Carrelage antidérapant qui résiste au gel pour profiter du soleil de la Cité du Soleil sans mauvaise surprise.",
  },
];


const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Accueil",
      "item": "https://carreaux-bat.ch/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Sierre",
      "item": "https://carreaux-bat.ch/sierre"
    }
  ]
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Carrelage et Rénovation à Sierre, Valais",
  "description": "Pose de carrelage, rénovation de salle de bain et douche italienne à Sierre, Cité du Soleil. Artisan carreleur dans le Valais central.",
  "url": "https://carreaux-bat.ch/sierre",
  "mainEntity": {
    "@type": "LocalBusiness",
    "name": "Carreaux-Bat Valais",
    "telephone": "+41 77 511 75 52",
    "areaServed": {
      "@type": "City",
      "name": "Sierre",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Valais"
      }
    }
  }
};

export default function SierrePage() {
  return (
    <div className="min-h-screen">
      <SeoHead
        title="Entreprise de Carrelage & Carreleur à Sierre à Proximité | Carreaux-Bat Valais"
        description="Vous cherchez un carreleur à proximité de Sierre ? Entreprise de carrelage et société de carrelage à Sierre, Valais. Service de carrelage par carreleur près de chez vous à Sierre, Chippis, Salgesch, Venthône. Pose de carrelage, douche italienne, faïence, terrasse. Devis gratuit."
        keywords="carreleur à proximité de Sierre, entreprise de carrelage à proximité de Sierre, société de carrelage à proximité de Sierre, carreleur près de chez moi Sierre, entreprise de carrelage près de chez vous Sierre, service de carrelage à proximité Sierre, entreprise de carrelage Sierre, société de carrelage Sierre, service de carrelage Sierre, carreleur Sierre, pose de carrelage Sierre, rénovation salle de bain Sierre, douche italienne Sierre, carrelage Chippis, faïence Sierre Valais, artisan carreleur Cité du Soleil"
        canonical="https://carreaux-bat.ch/sierre"
        ogTitle="Carrelage & Rénovation à Sierre à Proximité — Artisan Carreleur en Valais"
        ogDescription="Vous cherchez un carreleur à proximité de Sierre ? Pose de carrelage, rénovation salle de bain et douche italienne à Sierre et environs. Devis gratuit sur place."
        ogImage="/images/sierre-hero.jpg"
        geoPosition="46.2929;7.5377"
        geoPlacename="Sierre, Valais, Suisse"
        geoRegion="CH-VS"
      schema={{
        "@context": "https://schema.org",
        "@graph": [webPageSchema, breadcrumbSchema]
      }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img
          src="/images/sierre-hero.jpg"
          alt="Carrelage Sierre Valais"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block mb-4 px-4 py-1.5 bg-amber-700/80 text-white text-xs font-medium tracking-widest uppercase rounded-full">
            La Cité du Soleil
          </span>
          <h1 className="text-5xl font-bold text-white mb-4">Entreprise de Carrelage & Carreleur à Sierre</h1>
          <p className="text-white/70 max-w-xl mx-auto text-sm leading-relaxed">
            Sierre et ses environs, on les connaît par cœur. Les vieilles bâtisses du centre, les villas ensoleillées des hauteurs, les appartements récents — on s'adapte.
          </p>
          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-white/50">
            <Link to="/" className="hover:text-amber-400 transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Sierre</span>
          </div>
        </div>
      </section>

      {/* Description longue */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-xs uppercase tracking-widest text-amber-700 font-medium">Sierre, Valais</span>
              <h2 className="mt-2 text-3xl font-bold text-stone-900 mb-6">Sierre, la cité du soleil valaisan</h2>
              <div className="space-y-4 text-sm text-stone-500 leading-relaxed">
                <p>
                  Sierre, surnommée la &quot;Cité du Soleil&quot;, est une ville dynamique de près de 17 000 habitants située au cœur du Valais, sur la rive droite du Rhône. Avec plus de 2 000 heures d&apos;ensoleillement par an, Sierre bénéficie d&apos;un climat particulièrement clément qui attire habitants et visiteurs tout au long de l&apos;année.
                </p>
                <h3 className="text-lg font-bold text-stone-800 mt-6 mb-2">Carreleur à Sierre — Pose de carrelage et douche italienne</h3>
                <p>
                  La ville est le centre viticole du Valais par excellence. Les coteaux en terrasses qui dominent Sierre produisent des vins d&apos;exception, notamment dans les célèbres vignobles de Clavau, Glis et Villa. Le Bisse de Clavau, canal d&apos;irrigation historique, traverse les vignobles et offre une balade bucolique unique avec des vues panoramiques sur la plaine du Rhône.
                </p>
                <p>
                  Sierre est également une ville culturelle et éducative de premier plan. Elle abrite le siège de l&apos;HES-SO Valais-Wallis, la Médiathèque Valais — la plus grande bibliothèque de Suisse romande — ainsi que le Musée de la Nature. La ville est bilingue, à cheval entre le monde francophone et germanophone, ce qui lui confère une atmosphère cosmopolite unique en Valais.
                </p>
                <h3 className="text-lg font-bold text-stone-800 mt-6 mb-2">Entreprise de carrelage à Sierre — Rénovation complète</h3>
                <p>
                  Sur le plan immobilier, Sierre connaît une effervescence notable avec de nombreux projets de rénovation et de construction. Les quartiers historiques de Glis et de Bourg, ainsi que les nouveaux secteurs résidentiels, offrent une grande diversité architecturale. Que vous possédiez un appartement contemporain dans un immeuble récent ou une maison traditionnelle dans les vieux quartiers, on adapte la pose de carrelage à l'identité de votre bien.
                </p>
                <h3 className="text-lg font-bold text-stone-800 mt-6 mb-2">Société de carrelage à Sierre et dans le Valais central</h3>
                <p>
                  Notre équipe intervient régulièrement à Sierre et dans les communes limitrophes : Chippis, Salgesch, Venthône, Montana. On connaît les constructions du coin et les contraintes du climat valaisan. Envoyez-nous un message, on vient vous voir.
                </p>
              </div>
            </div>
            <div>
              <div className="relative rounded-lg overflow-hidden h-96 mb-6">
                <img
                  src="/images/sierre-hero.jpg"
                  alt="Réalisation carrelage Sierre"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 rounded-lg p-5 border border-amber-100">
                  <p className="text-2xl font-bold text-amber-700">17 000</p>
                  <p className="text-xs text-stone-500 mt-1">Habitants à Sierre</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-5 border border-stone-100">
                  <p className="text-2xl font-bold text-stone-900">500 m</p>
                  <p className="text-xs text-stone-500 mt-1">Altitude</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-5 border border-stone-100">
                  <p className="text-2xl font-bold text-stone-900">48h</p>
                  <p className="text-xs text-stone-500 mt-1">Délai d&apos;intervention</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-5 border border-amber-100">
                  <p className="text-2xl font-bold text-amber-700">Gratuit</p>
                  <p className="text-xs text-stone-500 mt-1">Devis sur place</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services locaux */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-widest text-amber-700 font-medium">Prestations locales</span>
            <h2 className="mt-3 text-3xl font-bold text-stone-900">Nos services à Sierre</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {localServices.map((service) => (
              <div key={service.title} className="bg-white rounded-lg p-6 border border-stone-100 hover:border-amber-200 transition-colors">
                <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-lg mb-4">
                  <i className={`${service.icon} text-amber-700 text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-stone-900 mb-2">{service.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{service.desc}</p>
              </div>
            ))}
            <div className="bg-amber-700 rounded-lg p-6 flex flex-col justify-between">
              <div>
                <h3 className="text-base font-semibold text-white mb-2">On vient vous voir à Sierre</h3>
                <p className="text-sm text-amber-100 leading-relaxed">Envoyez-nous un message, on passe vous voir à Sierre. On regarde votre espace, on discute, et on vous dit franchement ce qu'on peut faire.</p>
              </div>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-amber-700 text-sm font-medium rounded-full hover:bg-amber-50 transition-colors whitespace-nowrap"
              >
                Demander un devis
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Votre projet à Sierre</h2>
          <p className="text-stone-400 text-sm mb-8 max-w-xl mx-auto">
            De Sierre à Salgesch, en passant par Chippis et Venthône, on déplace nos outils partout où il y a du carrelage à poser. On passe vous voir, on évalue, on vous dit franchement ce qu'on peut faire.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-amber-700 text-white font-medium rounded-full hover:bg-amber-600 transition-colors whitespace-nowrap"
            >
              Devis gratuit à Sierre
            </Link>
            <a
              href="tel:+41775117552"
              className="px-8 py-3.5 border border-stone-600 text-stone-300 font-medium rounded-full hover:border-amber-600 hover:text-amber-400 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line"></i>
              +41 77 511 75 52
            </a>
          </div>
        </div>
      </section>

      <LocalFaqSection
        city="Sierre"
        faqs={[
          {
            question: "Quel est le délai d'intervention à Sierre ?",
            answer: "On intervient généralement sous 24 à 48h à Sierre et dans les communes voisines. La Cité du Soleil est à moins de 10 minutes de notre atelier à Grône, ce qui nous permet d'être réactifs pour les devis et les chantiers."
          },
          {
            question: "Faites-vous des travaux de carrelage à Chippis et Salgesch ?",
            answer: "Oui, on intervient dans tout le secteur de Sierre : Chippis, Salgesch, Venthône, et même jusqu'à Montana. C'est notre zone d'intervention principale, on connaît les constructions et les fournisseurs de la région."
          },
          {
            question: "Quels matériaux recommandez-vous pour le climat de la Cité du Soleil ?",
            answer: "Avec plus de 2 000 heures d'ensoleillement par an à Sierre, les UV sont intenses. On recommande des carrelages avec un bon indice de résistance UV, surtout pour les terrasses et les espaces exposés. En intérieur, le choix est large selon votre style."
          },
          {
            question: "Travaillez-vous avec des particuliers et des professionnels à Sierre ?",
            answer: "On travaille avec les deux. Particuliers pour des rénovations de salles de bain et des poses de carrelage, professionnels pour des chantiers plus importants. On adapte notre approche à la taille du projet et aux contraintes du client."
          },
          {
            question: "Comment se passe une rénovation complète de salle de bain à Sierre ?",
            answer: "On commence par une visite sur place pour évaluer l'existant. Ensuite on démonte l'ancien, on refait les installations si besoin, on pose le carrelage mural et au sol, et on installe le mobilier et la robinetterie. Vous n'avez qu'un seul interlocuteur du début à la fin."
          },
          {
            question: "Proposez-vous des finitions design pour les appartements modernes de Sierre ?",
            answer: "Absolument. On travaille avec des grands formats, des joints invisibles, des mosaïques design et des matériaux contemporains. Sierre compte de nombreux appartements modernes dans les nouveaux quartiers, et on sait livrer des finitions à la hauteur des attentes."
          }
        ]}
      />

      <Footer />
    </div>
  );
}
